/**
 * C1 v1.13: the Customer lens (C2 v1.15 `GET /api/customer-lens/{market}`, brief 3.3 CUSTOMER LENS). The Price Run's
 * Customer column and the drawer's Previous setpoint and Channel consistency lines read one row per priced own-channel
 * row, keyed as the grid rows are (xero_sku, marketplace_id, ALL for Shopify). Every figure is C2's string at the
 * committed price: the change on the price committed before it, the Amazon UK comparison, the converted price and the
 * rate. The browser works nothing out (H12); a comparison C2 cannot make shows C2's reason, never zero.
 * C1 v1.14: the newest entry's rule-verdict snapshot (C2 v1.16 `price.strategy_snapshot`) under the drawer's Price history.
 * C1 v1.20: the serving figures at the committed price (C2 v1.21 `price_per_serving`, owner rulings S-B to S-E): per serving and
 * per month in the column, all four in the drawer, each C2's string or C2's reason.
 * C1 v1.61 (C2 v1.57 `staged`, Phase 5 P8, CL-010): a row whose price is saved in the market's open run shows the same figures at
 * the saved price, marked as not live; a saved row with no committed price comes in `staged_only_rows`.
 */
import { call, GRID_STALE_TIME, type Market } from "./api";
import { ABSENT, humanDate, money, sameInstant, signedPct } from "./format";
import type { VsAmazonUk } from "./projection";
import type { StrategySnapshot } from "./strategies";

/** The committed price against the one committed before it; `reason` when there is none or the two cannot be compared. */
export type VsPreviousLens = {
  value: string | null; previous_price: string | null; previous_currency: string | null; previous_committed_at: string | null;
  previous_source: string | null; reason: string | null;
};
/** C2 v1.21's serving figures at the committed price: money strings in `currency`, or null with the reason C2 names. */
export type ServingFigures = {
  value: string | null; per_day: string | null; per_month: string | null; per_unit: string | null; currency: string;
  servings_per_pack: string | null; max_daily_servings: string | null; units_per_pack: number | null; packs: number | null;
  reason: string | null; per_day_reason: string | null; per_unit_reason: string | null;
};
/** C2 v1.57: the figures at the price saved for the row in the open run; the committed price is the previous setpoint. */
export type StagedLens = {
  price: { value: string; currency: string; saved_at: string };
  vs_previous_price_pct: { value: string | null; previous_price: string | null; reason: string | null };
  vs_amazon_uk_pct: VsAmazonUk; price_per_serving: ServingFigures;
};
export type CustomerRow = {
  xero_sku: string; marketplace_id: string; product: string; variant: string; channel: "amazon" | "shopify";
  price: { value: string; currency: string; committed_at: string; source: string; strategy_snapshot: StrategySnapshot | null };
  vs_previous_price_pct: VsPreviousLens; vs_amazon_uk_pct: VsAmazonUk; price_per_serving: ServingFigures;
  /** Null when nothing is saved for the row; absent from an API before C2 v1.57. */
  staged?: StagedLens | null;
};
/** C2 v1.57: a row with a saved price and no committed one. */
export type StagedOnlyRow = {
  xero_sku: string; marketplace_id: string; product: string; variant: string; channel: "amazon" | "shopify"; price: null; staged: StagedLens;
};
export type CustomerLens = {
  market: Market; as_of: string; row_count: number; rows: CustomerRow[]; staged_only_rows?: StagedOnlyRow[]; method: string; notes: string[];
};

export const CUSTOMER_KEY = ["pricing", "customer-lens"] as const;
export const getCustomerLens = (market: Market) => call<CustomerLens>(`/customer-lens/${market}`);
/** One read per market, kept as long as the grid (five minutes). A commit moves both comparisons and a save or a remove can
 * move the Amazon UK anchor, so the commit bar and the New price box drop it with the batch. */
export function customerQuery(market: Market) {
  return { queryKey: [...CUSTOMER_KEY, market] as const, queryFn: () => getCustomerLens(market), retry: 1, staleTime: GRID_STALE_TIME };
}

export type CustomerRead = { lens: CustomerLens | null; failed: boolean };
export type CustomerReads = Partial<Record<Market, CustomerRead>>;
export type CustomerLookup = { kind: "pending" } | { kind: "failed" } | { kind: "none" } | { kind: "row"; row: CustomerRow }
  | { kind: "staged_only"; row: StagedOnlyRow };
/** The lens of one Price Run row by SKU and place, as the RRP overlay and the strategy evaluation are looked up; a ship-country
 * row carries its store's. No row: nothing is committed for the SKU there (the price cell names the missing price). */
export function customerLookup(reads: CustomerReads | undefined, market: Market, sku: string, place: string): CustomerLookup {
  const read = reads?.[market];
  if (!read || (!read.lens && !read.failed)) return { kind: "pending" };
  if (!read.lens) return { kind: "failed" };
  const row = read.lens.rows.find((r) => r.xero_sku === sku && r.marketplace_id === place);
  if (row) return { kind: "row", row };
  const stagedOnly = read.lens.staged_only_rows?.find((r) => r.xero_sku === sku && r.marketplace_id === place);
  return stagedOnly ? { kind: "staged_only", row: stagedOnly } : { kind: "none" };
}

export const CUSTOMER_COPY = {
  vsLast: "vs last set price",
  vsAmazonUk: "vs Amazon UK",
  na: "n/a",
  reading: "reading…",
  unread: "Customer figures could not be read",
  noCommitted: "no committed price to compare",
  was: (price: string) => `was ${price}`,
  anchorRow: "this is the Amazon UK row, the price the other rows compare with",
  perServing: (serving: string) => `${serving} per serving`,
  perMonth: (month: string) => `${month} per month`,
  servingNotSet: "per serving: not set in Config",
  monthNotSet: "per month needs the maximum daily servings",
  servingLabel: "Price per serving", dayLabel: "Price per day", monthLabel: "Price per month", unitLabel: "Price per unit",
  servingMeta: (servings: string, packs: number, saved: boolean) => `${servings} servings per pack${packs > 1 ? ` × ${packs} packs in the case` : ""} · at the ${saved ? "saved" : "committed"} price`,
  dayMeta: (max: string) => `at most ${max} servings a day`,
  monthMeta: (max: string) => `30 days at ${max} servings a day`,
  unitMeta: (units: number, packs: number) => `${units} units per pack${packs > 1 ? ` × ${packs} packs in the case` : ""}`,
  previous: "Previous setpoint",
  channel: "Channel consistency",
  previousMeta: (date: string, change: string, committed: string) => `set ${date} · ${change} at the committed ${committed}`,
  stagedMeta: (date: string, change: string, saved: string) => `set ${date} · ${change} at the saved ${saved}`,
  savedNotLive: (price: string) => `at the saved ${price} · Saved, not live`,
  anchor: (price: string, sku: string | null, pending: boolean) => `vs Amazon UK ${price}${pending ? " saved in the open UK run" : ""}${sku ? ` (${sku})` : ""}`,
  converted: (price: string, compared: string, date: string) => `${price} is ${compared} at the ECB rate of ${date}`,
  latest: "Latest:",
  before: "Before it:",
  oneEntry: "One entry:",
  noEarlier: "No earlier price in the log.",
};
const SOURCE_WORD: Record<string, string> = { legacy_sheet: "legacy price sheet", price_run: "Price Run" };

/** The Customer column of one table row (approved design M25 `custCellHtml`, at the committed price): the change on the last
 * set price with that price, or C2's reason; the Amazon UK comparison, or n/a with the reason in the title. */
export type CustomerCell = {
  previous: string; detail: string | null; amazonUk: string | null; amazonUkTitle: string | null; serving: string | null; servingTitle: string | null;
  /** C1 v1.61: the saved price the figures are at, marked not live; null at the committed price. */
  pending: string | null;
};
/** The column's serving line (ruling S-E): per serving and per month, or the part Config lacks, with C2's reason in the title. */
export function servingCell(s: ServingFigures): { serving: string; servingTitle: string | null } {
  if (s.value == null) return { serving: CUSTOMER_COPY.servingNotSet, servingTitle: s.reason };
  const month = s.per_month == null ? CUSTOMER_COPY.monthNotSet : CUSTOMER_COPY.perMonth(money(s.per_month, s.currency));
  return { serving: `${CUSTOMER_COPY.perServing(money(s.value, s.currency))} · ${month}`, servingTitle: s.per_month == null ? s.per_day_reason : null };
}
function amazonUkCell(sku: string, uk: VsAmazonUk): Pick<CustomerCell, "amazonUk" | "amazonUkTitle"> {
  const anchor = uk.anchor_sku === sku;
  return {
    amazonUk: `${CUSTOMER_COPY.vsAmazonUk} ${anchor || uk.value == null ? CUSTOMER_COPY.na : signedPct(uk.value)}`,
    amazonUkTitle: anchor ? CUSTOMER_COPY.anchorRow : uk.value == null ? uk.reason : null,
  };
}
/** The cell at the saved price (C1 v1.61): the change on the committed price "was" it, or C2's reason when there is none. */
function stagedCell(sku: string, staged: StagedLens): CustomerCell {
  const prev = staged.vs_previous_price_pct;
  return {
    previous: prev.value == null ? CUSTOMER_COPY.vsLast : `${CUSTOMER_COPY.vsLast} ${signedPct(prev.value)}`,
    detail: prev.value == null ? prev.reason : CUSTOMER_COPY.was(money(prev.previous_price, staged.price.currency)),
    ...amazonUkCell(sku, staged.vs_amazon_uk_pct),
    ...servingCell(staged.price_per_serving),
    pending: CUSTOMER_COPY.savedNotLive(money(staged.price.value, staged.price.currency)),
  };
}
/** The Customer column's one figure on the Price Run (CLB-022): the move against the previous setpoint, at the saved price when
 * one is saved, as `customerCell` reads it. Null when C2 names none; the reasons stay in the drawer's lens. */
/** True when the Customer figure on screen is at a saved price: C2's band (c2-row-bands, k579) is at the committed one. */
export function customerAtSaved(lookup: CustomerLookup): boolean {
  return lookup.kind === "staged_only" || (lookup.kind === "row" && Boolean(lookup.row.staged));
}
export function customerFigure(lookup: CustomerLookup): string | null {
  if (lookup.kind === "staged_only") return lookup.row.staged.vs_previous_price_pct.value == null ? null : signedPct(lookup.row.staged.vs_previous_price_pct.value);
  if (lookup.kind !== "row") return null;
  const prev = lookup.row.staged ? lookup.row.staged.vs_previous_price_pct : lookup.row.vs_previous_price_pct;
  return prev.value == null ? null : signedPct(prev.value);
}
export function customerCell(lookup: CustomerLookup): CustomerCell {
  if (lookup.kind === "staged_only") return stagedCell(lookup.row.xero_sku, lookup.row.staged);
  if (lookup.kind !== "row") {
    const detail = lookup.kind === "pending" ? CUSTOMER_COPY.reading : lookup.kind === "failed" ? CUSTOMER_COPY.unread : CUSTOMER_COPY.noCommitted;
    return { previous: CUSTOMER_COPY.vsLast, detail, amazonUk: null, amazonUkTitle: null, serving: null, servingTitle: null, pending: null };
  }
  const { row } = lookup;
  if (row.staged) return stagedCell(row.xero_sku, row.staged);
  const prev = row.vs_previous_price_pct;
  return {
    previous: prev.value == null ? CUSTOMER_COPY.vsLast : `${CUSTOMER_COPY.vsLast} ${signedPct(prev.value)}`,
    detail: prev.value == null ? prev.reason : CUSTOMER_COPY.was(money(prev.previous_price, prev.previous_currency)),
    ...amazonUkCell(row.xero_sku, row.vs_amazon_uk_pct),
    ...servingCell(row.price_per_serving),
    pending: null,
  };
}

/** One line of the drawer's Customer lens: the value (muted when it is not a figure) and the sentence under it. */
export type LensLine = { value: string; muted: boolean; meta: string };
function previousLine(row: CustomerRow): LensLine {
  if (row.staged) {
    // At the saved price the previous setpoint is the committed price, set when it was committed (C2 v1.57).
    const prev = row.staged.vs_previous_price_pct;
    const was = money(prev.previous_price, row.price.currency);
    if (prev.value == null) return { value: prev.previous_price == null ? ABSENT : was, muted: true, meta: prev.reason ?? ABSENT };
    return { value: was, muted: false, meta: CUSTOMER_COPY.stagedMeta(humanDate(row.price.committed_at), signedPct(prev.value), money(row.staged.price.value, row.staged.price.currency)) };
  }
  const prev = row.vs_previous_price_pct;
  if (prev.previous_price == null) return { value: ABSENT, muted: true, meta: prev.reason ?? ABSENT };
  const was = money(prev.previous_price, prev.previous_currency);
  if (prev.value == null) return { value: was, muted: true, meta: prev.reason ?? ABSENT };
  return { value: was, muted: false, meta: CUSTOMER_COPY.previousMeta(humanDate(prev.previous_committed_at), signedPct(prev.value), money(row.price.value, row.price.currency)) };
}
/** Channel consistency of a row at a price: the committed one, or the saved one (C1 v1.61). */
function channelLine(sku: string, uk: VsAmazonUk, price: { value: string; currency: string }): LensLine {
  if (uk.anchor_sku === sku) return { value: CUSTOMER_COPY.na, muted: true, meta: CUSTOMER_COPY.anchorRow };
  if (uk.value == null) return { value: ABSENT, muted: true, meta: uk.reason ?? ABSENT };
  const anchor = CUSTOMER_COPY.anchor(money(uk.anchor_price, uk.anchor_currency), uk.anchor_sku, uk.anchor_pending === true);
  const converted = uk.fx_rate != null
    ? ` · ${CUSTOMER_COPY.converted(money(price.value, price.currency), money(uk.compared_price, uk.anchor_currency), humanDate(uk.fx_rate_date))}` : "";
  return { value: signedPct(uk.value), muted: false, meta: `${anchor}${converted}` };
}
function entryText(value: string | null, currency: string | null, at: string | null, source: string | null): string {
  return `${money(value, currency)}, set ${humanDate(at)}, source ${source ? SOURCE_WORD[source] ?? source : ABSENT}`;
}
/** The drawer's Price history from the log's two newest entries (C2 v1.15), so it never contradicts the Previous setpoint: after
 * a committed line of the newest run, the entry before it; otherwise the newest entry and the one before it, or the one entry.
 * Null while the lens is unread or has no row, and the drawer keeps its sentence from the grid. */
export function historyText(lookup: CustomerLookup, committed: boolean): string | null {
  if (lookup.kind !== "row") return null;
  const { price, vs_previous_price_pct: prev } = lookup.row;
  const before = prev.previous_price == null ? null
    : `${CUSTOMER_COPY.before} ${entryText(prev.previous_price, prev.previous_currency, prev.previous_committed_at, prev.previous_source)}.`;
  if (committed) return before ?? CUSTOMER_COPY.noEarlier;
  const latest = entryText(price.value, price.currency, price.committed_at, price.source);
  return before ? `${CUSTOMER_COPY.latest} ${latest}. ${before}` : `${CUSTOMER_COPY.oneEntry} ${latest}.`;
}

/** The snapshot under the drawer's latest Price history entry (C1 v1.14): the lens row's newest entry's, as stored (null: none
 * recorded). After a commit of the newest run that entry is the committed line, so the snapshot is shown only once the lens has
 * re-read it (the two commit times name the same instant; C2 serves the log's in UTC and the run's in the session zone);
 * undefined while the lens is unread or stale, and the drawer then shows no verdict rather than another entry's. */
export function latestSnapshot(lookup: CustomerLookup, committedAt: string | null): StrategySnapshot | null | undefined {
  if (lookup.kind !== "row") return undefined;
  if (committedAt !== null && !sameInstant(lookup.row.price.committed_at, committedAt)) return undefined;
  return lookup.row.price.strategy_snapshot;
}

/** The drawer's four serving lines (ruling S-E): each C2's figure with what it counts, or ABSENT with C2's reason. */
export type ServingLines = { serving: LensLine; day: LensLine; month: LensLine; unit: LensLine };
function servingLines(s: ServingFigures, saved: boolean): ServingLines {
  const packs = s.packs ?? 1;
  const figure = (value: string | null, meta: string | null, reason: string | null): LensLine =>
    value == null ? { value: ABSENT, muted: true, meta: reason ?? ABSENT } : { value: money(value, s.currency), muted: false, meta: meta ?? "" };
  const max = s.max_daily_servings;
  return {
    serving: figure(s.value, s.servings_per_pack == null ? null : CUSTOMER_COPY.servingMeta(s.servings_per_pack, packs, saved), s.reason),
    day: figure(s.per_day, max == null ? null : CUSTOMER_COPY.dayMeta(max), s.per_day_reason),
    month: figure(s.per_month, max == null ? null : CUSTOMER_COPY.monthMeta(max), s.per_day_reason),
    unit: figure(s.per_unit, s.units_per_pack == null ? null : CUSTOMER_COPY.unitMeta(s.units_per_pack, packs), s.per_unit_reason),
  };
}

/** The drawer's Previous setpoint, Channel consistency and serving lines (M25 drawer 2983): C2's figures, or the state of the read.
 * At a saved price (C1 v1.61) the lines read C2's staged figures and `pending` names the saved price; else `pending` is null. */
export function lensLines(lookup: CustomerLookup): { previous: LensLine; channel: LensLine; serving: ServingLines; pending: string | null } {
  if (lookup.kind === "row" && !lookup.row.staged) {
    const { row } = lookup;
    return { previous: previousLine(row), channel: channelLine(row.xero_sku, row.vs_amazon_uk_pct, row.price), serving: servingLines(row.price_per_serving, false), pending: null };
  }
  if (lookup.kind === "row" || lookup.kind === "staged_only") {
    const staged = lookup.row.staged!;
    // A staged-only row has no committed price: C2 names why there is no previous setpoint.
    const previous = lookup.kind === "row" ? previousLine(lookup.row) : { value: ABSENT, muted: true, meta: staged.vs_previous_price_pct.reason ?? ABSENT };
    return {
      previous, channel: channelLine(lookup.row.xero_sku, staged.vs_amazon_uk_pct, staged.price), serving: servingLines(staged.price_per_serving, true),
      pending: CUSTOMER_COPY.savedNotLive(money(staged.price.value, staged.price.currency)),
    };
  }
  const meta = lookup.kind === "pending" ? CUSTOMER_COPY.reading : lookup.kind === "failed" ? CUSTOMER_COPY.unread : CUSTOMER_COPY.noCommitted;
  const line: LensLine = { value: ABSENT, muted: true, meta };
  return { previous: line, channel: line, serving: { serving: line, day: line, month: line, unit: line }, pending: null };
}
